import { Coins, Cpu, Inbox, Play, Server, Square, Workflow } from 'lucide-react';
import { useState } from 'react';
import {
  AuditEntry,
  AuditList,
  Button,
  CostMeter,
  DATA_STATES,
  DataState,
  IconButton,
  ListRow,
  LiveBadge,
  MetricTile,
  PILL_STATUSES,
  Panel,
  PipelineStage,
  PipelineStages,
  RowCell,
  RowList,
  RowTitle,
  STATUS_META,
  Segmented,
  SkeletonRows,
  StatusPill,
  THEME_OPTIONS,
  formatMoney,
} from '@orgus/ui';
import type { DataStateKind, PillStatus } from '@orgus/ui';
import { Caption, Spec } from './Spec.tsx';

const TREND = [12, 14, 13, 17, 16, 19, 22, 21, 24, 23, 27, 29];
const MIN = 60_000;

/** StatusPill in every state, both variants, and every theme side by side. */
function StatusSection() {
  return (
    <Panel title="StatusPill" sub="icon plus label, every state" className="xl:col-span-2" data-catalogue="status">
      <Spec label="Soft (default)">
        {PILL_STATUSES.map((s) => (
          <StatusPill key={s} status={s} />
        ))}
      </Spec>
      <Spec label="Plain, for rows">
        {PILL_STATUSES.map((s) => (
          <StatusPill key={s} status={s} variant="plain" />
        ))}
      </Spec>
      <Spec label="Medium and custom words">
        <StatusPill status="working" size="md" label="Running 3 of 5" />
        <StatusPill status="needs-you" size="md" label="Waiting for approval" />
        <StatusPill status="done" size="md" label="Passed" />
      </Spec>
      <Spec label="Icon only (tooltip)">
        {PILL_STATUSES.map((s) => (
          <StatusPill key={s} status={s} iconOnly />
        ))}
      </Spec>
      <Spec label="Every theme">
        <div className="grid w-full gap-2 lg:grid-cols-3">
          {THEME_OPTIONS.map((t) => (
            <div key={t.value} data-theme={t.value} className="flex flex-col gap-2 rounded-card border border-border bg-surface p-3 text-fg">
              <span className="text-caption text-fg-subtle">{t.label}</span>
              <div className="flex flex-wrap gap-1.5">
                {PILL_STATUSES.map((s) => (
                  <StatusPill key={s} status={s} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </Spec>
      <Spec label="Meaning">
        <dl className="grid w-full gap-x-6 gap-y-1 text-caption sm:grid-cols-2 xl:grid-cols-3">
          {PILL_STATUSES.map((s: PillStatus) => (
            <div key={s} className="flex gap-2">
              <dt className="w-20 shrink-0 font-medium text-fg-muted">{STATUS_META[s].label}</dt>
              <dd className="text-fg-subtle">{STATUS_META[s].meaning}</dd>
            </div>
          ))}
        </dl>
      </Spec>
    </Panel>
  );
}

function MetricSection() {
  const now = Date.now();
  return (
    <Panel title="MetricTile" sub="value, unit, delta, sparkline, link" className="xl:col-span-2" data-catalogue="metric">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile label="Sessions running" icon={<Play />} value={7} unit="sessions" delta={{ value: 2, kind: 'absolute', goodWhen: 'neither', period: 'vs 1 h ago' }} trend={TREND} trendLabel="last 12 hours" href="#sessions" updatedAt={now - 4_000} />
        <MetricTile label="Spend today" icon={<Coins />} value="$4.20" delta={{ value: 0.12, goodWhen: 'down', period: 'vs yesterday' }} trend={TREND} trendLabel="last 12 hours" href="#spend" updatedAt={now - 4_000} />
        <MetricTile label="Pipeline success" icon={<Workflow />} value={96.4} unit="%" delta={{ value: 0.031, goodWhen: 'up', period: 'vs last week' }} trend={[90, 91, 92, 91, 93, 94, 93, 95, 95, 96, 96, 96.4]} href="#pipelines" updatedAt={now - 4_000} />
        <MetricTile label="Tokens today" icon={<Cpu />} value={1_420_000} unit="tokens" delta={{ value: 0, period: 'vs yesterday' }} trend={TREND} updatedAt={now - 4_000} />
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div>
          <Caption>Loading</Caption>
          <MetricTile label="Machines online" value={null} state="loading" />
        </div>
        <div>
          <Caption>Error</Caption>
          <MetricTile label="Machines online" value={null} state="error" />
        </div>
        <div>
          <Caption>Empty</Caption>
          <MetricTile label="Approvals today" value={null} emptyText="No approvals yet today" />
        </div>
        <div>
          <Caption>Stale (over 2 minutes)</Caption>
          <MetricTile label="Machines online" icon={<Server />} value={3} unit="of 4" updatedAt={now - 5 * MIN} />
        </div>
      </div>
    </Panel>
  );
}

function CostSection() {
  return (
    <Panel title="CostMeter" sub="tokens and money against a budget" data-catalogue="cost">
      <div className="grid gap-5 sm:grid-cols-2">
        <CostMeter label="Qawl-Agents" period="this month" cost={18.4} budget={50} tokens={2_840_000} />
        <CostMeter label="Fix login race" cost={4.1} budget={5} tokens={612_000} estimated />
        <CostMeter label="Nightly verify" period="today" cost={11.2} budget={10} tokens={1_204_000} />
        <CostMeter label="Docs v2" cost={0.42} tokens={48_200} />
        <CostMeter label="Session spend" state="loading" />
      </div>
      <Caption>
        <span className="mt-4 block">Small, for rows and drawers</span>
      </Caption>
      <div className="flex flex-col gap-2">
        <CostMeter label="Fix login race" size="sm" cost={1.2} budget={5} />
        <CostMeter label="Fix login race" size="sm" cost={4.4} budget={5} />
        <CostMeter label="Fix login race" size="sm" cost={6.1} budget={5} />
        <CostMeter label="Fix login race" size="sm" tokens={48_200} />
      </div>
    </Panel>
  );
}

function DataStateSection() {
  const [state, setState] = useState<DataStateKind>('partial');
  const empty = { icon: <Inbox />, title: 'No machines yet', description: 'Connect a machine to see its sessions and capacity here.', action: <Button size="sm">Connect a machine</Button> };
  const rows = (
    <RowList label="Machines">
      <ListRow lead={<StatusPill status="idle" variant="plain" iconOnly />}>
        <RowTitle title="studio" meta="Windows 11, 3 sessions" />
        <RowCell numeric width="w-16">42% CPU</RowCell>
      </ListRow>
      <ListRow lead={<StatusPill status="offline" variant="plain" iconOnly />}>
        <RowTitle title="build-01" meta="Last seen 4m ago" muted />
        <RowCell numeric width="w-16">No data</RowCell>
      </ListRow>
    </RowList>
  );
  const props = {
    empty,
    partial: '1 machine offline, so its numbers may be stale.',
    error: { what: 'Could not load machines.', why: 'The hub did not answer within 10 s.', next: 'Check that Orgus is running, then try again.' },
    onRetry: () => new Promise<void>((r) => setTimeout(r, 600)),
    skeleton: <SkeletonRows rows={2} />,
  };
  return (
    <Panel
      title="DataState"
      sub="loading, empty, partial, error, full"
      className="xl:col-span-2"
      data-catalogue="datastate"
      actions={
        <Segmented
          label="Data state"
          value={state}
          onChange={setState}
          options={DATA_STATES.map((s) => ({ value: s, label: s[0]?.toUpperCase() + s.slice(1) }))}
        />
      }
    >
      <div className="rounded-card border border-border">
        <DataState state={state} {...props}>
          {rows}
        </DataState>
      </div>
      <Caption>
        <span className="mt-4 block">All five at once</span>
      </Caption>
      <div className="grid gap-3 lg:grid-cols-5">
        {DATA_STATES.map((s) => (
          <div key={s} className="min-w-0">
            <Caption>{s}</Caption>
            <div className="overflow-hidden rounded-card border border-border">
              <DataState state={s} {...props} empty={{ ...empty, action: undefined, description: 'Connect your first machine.' }} error={{ ...props.error, next: 'Check that Orgus is running.' }}>
                {rows}
              </DataState>
            </div>
          </div>
        ))}
      </div>
      <Spec label="Live" className="mt-2">
        <LiveBadge mode="live" />
        <LiveBadge mode="paused" />
        <LiveBadge mode="reconnecting" />
        <LiveBadge mode="offline" />
      </Spec>
    </Panel>
  );
}

function AuditSection() {
  const now = Date.now();
  return (
    <Panel title="AuditEntry" sub="who, what, where, when, signed hash" data-catalogue="audit">
      <AuditList>
        <AuditEntry actor={{ name: 'Srikanth', kind: 'person' }} action="stopped session" target="Fix login race" where={['studio', 'Qawl-Agents']} at={now - 4 * MIN} hash="9f2c4e1ab37d55aa0e41c2" verified />
        <AuditEntry
          actor={{ name: 'triage-bot', kind: 'agent' }}
          action="tried to merge"
          target="feat/approvals-inbox"
          where={['build-01']}
          at={now - 22 * MIN}
          result="denied"
          message="Policy main-is-protected needs a person to approve merges to main."
          hash="03b7aa91c4e2f01d77"
        />
        <AuditEntry actor={{ name: 'Orgus', kind: 'system' }} action="removed worktree" target="qawl-wt-voice-latency" where={['studio']} at={now - 3 * 60 * MIN} result="failed" message="The folder has 6 changed files." />
      </AuditList>
    </Panel>
  );
}

function PipelineSection() {
  return (
    <Panel title="PipelineStage" sub="name, status, duration, gate" data-catalogue="pipeline">
      <PipelineStages label="Landing pipeline">
        {[
          <PipelineStage key="build" name="Build" status="done" statusLabel="Passed" durationMs={48_000} gate={{ label: 'Typecheck and tests', state: 'passed' }} />,
          <PipelineStage key="verify" name="Verify" status="working" statusLabel="Running 3 of 5" durationMs={192_000} count={5} current />,
          <PipelineStage key="land" name="Land" status="needs-you" gate={{ label: 'Approval from Srikanth', state: 'waiting' }} />,
        ]}
      </PipelineStages>
      <div className="mt-3">
        <PipelineStages label="Failed pipeline">
          {[
            <PipelineStage key="build" name="Build" status="failed" durationMs={31_000} gate={{ label: 'Tests must pass', state: 'blocked' }} />,
            <PipelineStage key="deploy" name="Deploy" status="queued" gate={{ label: 'After build', state: 'locked' }} />,
          ]}
        </PipelineStages>
      </div>
    </Panel>
  );
}

function RowSample({ density }: { density: 'comfortable' | 'compact' }) {
  const [tick, setTick] = useState(0);
  const rows: Array<{ status: PillStatus; title: string; meta: string; elapsed: string; cost: number }> = [
    { status: 'working', title: 'Fix login race', meta: 'Qawl-Agents · studio · feat/login', elapsed: '12m', cost: 1.2 + tick * 0.05 },
    { status: 'needs-you', title: 'Approvals inbox', meta: 'Orgus · mbp-16', elapsed: '4m', cost: 0.42 },
    { status: 'idle', title: 'Webhook tests', meta: 'Qawl-Agents · studio', elapsed: '1h 05m', cost: 3.9 },
    { status: 'ended', title: 'Docs v2', meta: 'Orgus · studio', elapsed: '2h 10m', cost: 0.18 },
  ];
  return (
    <div data-density={density} className="min-w-0">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-caption text-fg-subtle">{density === 'compact' ? 'Compact, 32 px rows' : 'Comfortable, 40 px rows'}</span>
        <Button size="sm" variant="ghost" onClick={() => setTick((t) => t + 1)}>
          Simulate a live update
        </Button>
      </div>
      <div className="overflow-hidden rounded-card border border-border bg-surface">
        <RowList label={`Sessions, ${density}`}>
          {rows.map((r, i) => (
            <ListRow
              key={r.title}
              lead={<StatusPill status={r.status} variant="plain" iconOnly />}
              selected={i === 1}
              onOpen={() => {}}
              highlightKey={i === 0 ? tick : undefined}
              actions={<IconButton size="xs" label={`Stop ${r.title}`} icon={<Square />} />}
              trail={
                <RowCell numeric width="w-14">
                  {formatMoney(r.cost)}
                </RowCell>
              }
            >
              <RowTitle title={r.title} meta={density === 'comfortable' ? r.meta : undefined} muted={r.status === 'ended'} />
              <RowCell hideBelow="md" width="w-24">
                <StatusPill status={r.status} variant="plain" />
              </RowCell>
              <RowCell numeric width="w-20">
                {r.elapsed}
              </RowCell>
            </ListRow>
          ))}
        </RowList>
      </div>
    </div>
  );
}

function RowSection() {
  return (
    <Panel title="Rows" sub="ListRow, RowTitle, RowCell" className="xl:col-span-2" data-catalogue="rows">
      <div className="grid gap-5 lg:grid-cols-2">
        <RowSample density="comfortable" />
        <RowSample density="compact" />
      </div>
    </Panel>
  );
}

/** The domain components (DesignSystem 3.2) in every state. */
export function ComponentCatalogue() {
  return (
    <div className="grid items-start gap-5 xl:grid-cols-2">
      <StatusSection />
      <MetricSection />
      <DataStateSection />
      <RowSection />
      <CostSection />
      <PipelineSection />
      <AuditSection />
    </div>
  );
}
